import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { registerUser } from "../../shared/_services/auth.services";
import { setChallengeToken } from "../../shared/_helpers/otpChallenge";
import { getCountryCode } from "../../shared/_services/common.services";
import MainLogo from "../../assets/images/AstraLogo.svg";

function resolveInvalidClass(hasError) {
  return hasError ? "is-invalid" : "";
}

function resolveFormControlClass(hasError) {
  return hasError ? "form-control is-invalid" : "form-control";
}

function resolvePasswordInputType(isVisible) {
  return isVisible ? "text" : "password";
}

function resolvePasswordIconClass(isVisible) {
  return isVisible ? "iconoir-eye-closed" : "iconoir-eye";
}

function resolveSubmitButtonLabel(isSubmitting) {
  return isSubmitting ? "Registering..." : "Register";
}


const SignUp = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countrycode, setCountryCodes] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const fetchCountryCodes = async () => {
      const code = await getCountryCode();

      setCountryCodes(code?.data);
    };
    fetchCountryCodes();
  }, []);

  const initialValues = {
    name: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    code: "+1",
    acceptTerms: false
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]*$/, "Name cannot contain numbers") // Ensure no numbers
      .test("no-leading-space", "Name cannot start with a space", (value) => {
        // Check if value starts with space, return false if it does
        return !value.startsWith(" ");
      }) // Ensure no numbers
      .test("valid-name", "Enter a valid name", (value) => {
        return !!value && value.trim().length > 0; // Ensure at least 1 valid character
      })
      .min(3, "Name must be at least 3 characters")
      .max(30, "Name must be at most 30 characters"),
    email: Yup.string()
      .required("Email is required")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
    mobile: Yup.string()
      .required("Phone number is required")
      .matches(
        /^(?!.*(\d)\1{2}-(\d)\2{2}-(\d)\3{3})\d{3}-\d{3}-\d{4}$/,
        "Invalid phone number format or repeated digits"
      ),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
        "Password should contain at least 1 number, 1 lowercase, 1 uppercase, 1 special character"
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {

    if (!values.acceptTerms) {
      enqueueSnackbar("You must accept the privacy policy to register", {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setSubmitting(false);
      return; // Stop submission
    }
    const params = new URLSearchParams(location.search);
    const loginType = params.get('loginType');
    const { name, email, mobile, password, code, acceptTerms } = values;


    const [firstName, ...lastNameArray] = name.split(" ");
    const lastName = lastNameArray.join(" ");
    let data = await registerUser({ firstName, lastName, email, mobile, password, userType: "client", code, loginType, acceptTerms });
    if (data.status === 200) {
      let currentUser = { user: data?.data?.data, token: data?.data?.data?.token };
      delete currentUser.user.token;

      // Signup sends a verification code of its own and lands the new client on
      // /verify-code, so it also returns the challenge token that redeems it
      // (backend SEC-114). A flow credential, not part of the identity —
      // sessionStorage, and out of the persisted user.
      setChallengeToken(currentUser.user.challengeToken);
      delete currentUser.user.challengeToken;

      window.localStorage.setItem("currentUser", JSON.stringify(currentUser));
      if (currentUser?.user?.userType === 'client') {
        sessionStorage.setItem('verifyCodeFrom', 'signup');
        navigate('/verify-code', { state: { from: 'signup' } });
      }
    }
    else {
      enqueueSnackbar(data?.response?.data?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setSubmitting(false);
    }
  };

  return (
    <div className="login-signup-wrap">
      <div className="container">
        <div className="row">
          <div className="col-md-12 col-lg-7 logo-wrap place-content-center">
            <a className="text-left d-block" href="/">
              <img
                className="login-signup-logo"
                src={MainLogo}
                alt="Astra Logo"
              />
            </a>
            <div className="login-signup-video mobile-hidden-text">
              <div className="onboarding-vdo-login">
                <video src={`${process.env.REACT_APP_BACKEND}/uploads/WelcometoAstraStars.mp4`} controls loop muted autoPlay></video>
              </div>
              <h2 className="auth-title">Financial Guidance Built Around You</h2>
              <p className="auth-description">Work directly with a dedicated financial advisor who understands your goals — supported by modern tools that keep your plan organized, clear, and always accessible.</p>
              <small className="auth-pointers">• Personal Advisor • Secure Platform • Smart Insights</small>
            </div>
          </div>
          <div className="col-md-12 col-lg-5 place-content-center">
            <div className="login-signup-box signup-box mt-5">
              <h2 className="login-signup-head text-left mb-2">Signup</h2>
              <p className="login-signup-subhead mb-4">Let’s Get You Started</p>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                  <Form>
                    <div className="form-group">
                      <Field
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Full Name"
                        className={resolveFormControlClass(Boolean(errors.name && touched.name))}
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <div className="form-group input-phone-con">
                          <div className="d-flex flex-wrap w-100">
                            <Field as="select" name="code" disabled
                              className={resolveInvalidClass(Boolean(errors.mobile && touched.mobile))}
                            >
                              {countrycode?.map((item) => (
                                <option key={item.code} value={item.code}>
                                  ({item.code})
                                </option>
                              ))}
                            </Field>

                            <Field
                              type="text"
                              id="mobile"
                              name="mobile"
                              placeholder="Phone Number"
                              className={resolveFormControlClass(Boolean(errors.mobile && touched.mobile))}
                              value={formatPhoneNumber(values.mobile)}
                              onChange={e => setFieldValue("mobile", formatPhoneNumber(e.target.value))}
                            />
                            <ErrorMessage
                              name="mobile"
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Email Address"
                        className={resolveFormControlClass(Boolean(errors.email && touched.email))}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="row">
                      <div className="col-md-6 pr-1">
                        <div className="form-group password-field">
                          <Field
                            type={resolvePasswordInputType(showPassword)}
                            name="password"
                            id="password"
                            placeholder="Password"
                            className={resolveFormControlClass(Boolean(errors.password && touched.password))}
                          />
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="invalid-feedback"
                          />
                          <span
                            className="toggle-password"
                            onClick={() => { setShowPassword(!showPassword) }}
                          >
                            <i className={resolvePasswordIconClass(showPassword)}></i>
                          </span>
                        </div>
                      </div>
                      <div className="col-md-6 pl-1">
                        <div className="form-group password-field">
                          <Field
                            type={resolvePasswordInputType(showConfirmPassword)}
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="Confirm Password"
                            className={resolveFormControlClass(Boolean(errors.confirmPassword && touched.confirmPassword))}
                          />
                          <ErrorMessage
                            name="confirmPassword"
                            component="div"
                            className="invalid-feedback"
                          />
                          <span
                            className="toggle-password"
                            onClick={() => { setShowConfirmPassword(!showConfirmPassword) }}
                          >
                            <i className={resolvePasswordIconClass(showConfirmPassword)}></i>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="form-group mt-2 d-flex text-light f-400 text-14 align-items-center">
                      <Field type="checkbox" name="acceptTerms" style={{ height: "16px", width: "16px" }} />&nbsp;&nbsp; By signing up you agree to our{" "}&nbsp;
                      <a className="text-light f-500" href="https://www.astrawmg.com/privacy-policy/" target="_blank"><u>Privacy policy</u></a>.
                    </div>
                    <button
                      type="submit"
                      className="auth-screen-btn mt-2"
                      disabled={isSubmitting}
                    >
                      {resolveSubmitButtonLabel(isSubmitting)}
                    </button>
                    <p className="text-center text-light mt-4 mobile-hidden-text f-500 text-14">
                      Already a member?{" "}
                      <NavLink className="text-light f-500" to="/login">
                        <u>Login here</u>
                      </NavLink>
                    </p>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
